import { z } from 'zod';
import { bboxSchema, latitudeSchema, longitudeSchema, mapLocationSchema } from './common';

/**
 * Address search, normalized from the Mapbox Geocoding v6 API.
 *
 * The Worker owns the Mapbox call so the token stays in one place and results
 * can be edge-cached. It also flattens Mapbox's GeoJSON into the small shape
 * below, so the client depends on our contract rather than theirs.
 */

/** `GET /api/geocode` query params. */
export const geocodeQuerySchema = z.object({
  q: z.string().trim().min(1).max(256),
  /**
   * `lon,lat` to bias results toward — usually the map's current centre, which
   * is what makes searching "main st" return the one nearby.
   */
  proximity: z
    .string()
    .regex(/^-?\d+(\.\d+)?,-?\d+(\.\d+)?$/, 'proximity must be "lon,lat"')
    .optional(),
  limit: z.coerce.number().int().min(1).max(10).default(5),
});
export type GeocodeQuery = z.infer<typeof geocodeQuerySchema>;

/** `GET /api/geocode/reverse` query params. */
export const reverseGeocodeQuerySchema = z.object({
  longitude: z.coerce.number().pipe(longitudeSchema),
  latitude: z.coerce.number().pipe(latitudeSchema),
});
export type ReverseGeocodeQuery = z.infer<typeof reverseGeocodeQuerySchema>;

/** One search result: a labelled point, plus the extras the map can use. */
export const geocodePlaceSchema = mapLocationSchema.extend({
  /** Stable id from Mapbox, used as the React key and for de-duplication. */
  id: z.string().min(1),
  /** The short name, e.g. "Denver" or "1600 Pennsylvania Ave NW". */
  name: z.string().min(1),
  /** Everything after the name, e.g. "Colorado, United States". May be empty. */
  context: z.string(),
  /** Present for areas (cities, states); absent for point addresses. */
  bbox: bboxSchema.optional(),
});
export type GeocodePlace = z.infer<typeof geocodePlaceSchema>;

export const geocodeResponseSchema = z.object({
  places: z.array(geocodePlaceSchema),
});
export type GeocodeResponse = z.infer<typeof geocodeResponseSchema>;

/** Reverse geocoding returns at most one place — null when nothing matched. */
export const reverseGeocodeResponseSchema = z.object({
  place: geocodePlaceSchema.nullable(),
});
export type ReverseGeocodeResponse = z.infer<typeof reverseGeocodeResponseSchema>;
